import { Fragment, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Skeleton, Typography } from "@mui/material"

import { REDIRECT_PATH_STORAGE_ITEM } from "../../constants";
import MainContainer from "../../components/Container/MainContainer"
import AppContentHeader from "../../components/AppContentHeader";
import BaseImage from "../../components/BaseImage"
import BlockContainer from "../../components/Container/BlockContainer"
import MedicationSummary from "../../components/PageMedicationReport/MedicationSummary";
import MedicationDetails from "../../components/PageMedicationReport/MedicationDetails";

import IconMedicine from "../../assets/images/reports-icons/Pill.svg";
import { setMedicationFilterOptions } from "../../store";
import ReportReviewBottomNav from "../../components/ReportReviewBottomNav";
import { findMemberByUserId } from "../../utils/member-lookup";

const TAB_REPORT = [
  {
    title: 'Summary'
  },
  {
    title: 'Details'
  }
]

const MedicationReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams()
  const members = useSelector(state => state.members)
  const loadingAuth = useSelector(state => state.loadingAuth)
  const selectedPatient = useMemo(() => findMemberByUserId(members, id), [members, id])
  const reports = useSelector(state => state.patientReports)
  const medicationData = useMemo(() => reports?.find(item => item.name === 'Medications'), [reports])
  const userMedications = useMemo(() => selectedPatient?.user?.medications, [selectedPatient])
  
  const [selectedTab, setSelectedTab] = useState(TAB_REPORT[0].title)
  const [selectedLevel, setSelectedLevel] = useState('low')

  const getReportFileName = () => {
    return selectedPatient?.user?.kits?.[0]?.accessionId + '-Medications.pdf'
  }
  const currentMedications = useMemo(() => {
    if (medicationData?.data?.medications) {
      const userMedicationNameList = userMedications ? userMedications.map(item => item.name.toLowerCase().split(' ')?.[0] || '') : []

      const result = []
      medicationData?.data?.medications.forEach(medication => {
        userMedicationNameList.forEach((name => {
          if (medication.name.toLowerCase().includes(name) || medication.brand_names.toString().toLowerCase().includes(name)) {
            result.push(medication)
          }
        }))
      })
    
      return result.map((item => {
        let level = 'Low'
        let percent = 15
        if (parseInt(item.guidanceLevel) === 2) {
          level = 'Moderate'
          percent = 50
        }
        if (parseInt(item.guidanceLevel) === 3) {
          level = 'High'
          percent = 95
        }
        
        let descriptionImage = ''
        if (item.description.includes('href')) {
          let temp = document.createElement("div");
          temp.innerHTML = item.description;
          const nodeMap = temp.firstElementChild.attributes;
          let value = nodeMap.getNamedItem("href").value;
          descriptionImage = value
        }

        return (
          {
            title: item.name,
            category: item.category,
            guidanceLevel: level,
            percent: percent,
            descriptionTitle: item.phenotype,
            description: item.description,
            descriptionImage,
            brandNames: item.brand_names,
            genes: item.genes,
            class: item.class,
            evidenceLevel: item.evidenceLevel
          }
        )
      })).sort((a, b) => a.title.localeCompare(b.title))
    } else return []
  }, [medicationData, userMedications])

  const medications = useMemo(() => {
    if (medicationData?.data?.medications && medicationData?.data?.medications.length > 0) {
      return medicationData?.data?.medications.map((item => {
        let level = 'Low'
        let percent = 15
        if (parseInt(item.guidanceLevel) === 2) {
          level = 'Moderate'
          percent = 50
        }
        if (parseInt(item.guidanceLevel) === 3) {
          level = 'High'
          percent = 95
        }

        let descriptionImage = ''
        if (item.description.includes('href')) {
          let temp = document.createElement("div");
          temp.innerHTML = item.description;
          const nodeMap = temp.firstElementChild.attributes;
          let value = nodeMap.getNamedItem("href").value;
          descriptionImage = value
        }

        return (
          {
            title: item.name,
            category: item.category,
            guidanceLevel: level,
            percent: percent,
            descriptionTitle: item.phenotype,
            description: item.description,
            descriptionImage,
            brandNames: item.brand_names,
            genes: item.genes,
            class: item.class,
            evidenceLevel: item.evidenceLevel
          }
        )
      })).sort((a, b) => a.title.localeCompare(b.title))
    } else return []
  }, [medicationData])

  const handleClickRiskCounts = (count) => {
    setSelectedTab('Details')
    setSelectedLevel(count.level)
    const options = {
      riskFactorLevels: [count.level]
    }
    dispatch(setMedicationFilterOptions(options))
  }

  const handleClickSearch = (searchKey) => {
    setSelectedTab('Details')
    const options = {
      searchKey
    }
    dispatch(setMedicationFilterOptions(options))
  }

  const handleBack = () => {
    if (selectedTab === 'Summary') {
      const savedRedirectPath = decodeURIComponent(localStorage.getItem(REDIRECT_PATH_STORAGE_ITEM));
      const currentPath = decodeURIComponent(window.location.pathname);
      if(savedRedirectPath === currentPath) {
        localStorage.removeItem(REDIRECT_PATH_STORAGE_ITEM);
        navigate('/')
      } else {
        navigate(-1)
      }
    } else {
      setSelectedTab('Summary')
    }
  }

  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'relative'
        }}
      >
        <Box className='content-header-wave-white'/>
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            mt: '-90px',
            padding: '16px 20px',
          }}
        >
        <BlockContainer>
          <AppContentHeader 
            backLabel=''
            hasBackLink
            onBack={handleBack}
            pdfDownloadUrl={medicationData?.pdfUrl}
            pdfName={getReportFileName()}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '120px 0 38px',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h1">
                Medication Report
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  mt: '16px'
                }}
              >
                <Typography
                  variant="body"
                  sx={{
                    color: 'primary.darkGray'
                  }}
                >
                  Member:
                </Typography>
                {loadingAuth ? (
                  <Skeleton variant="rounded" width={80} height={24} />
                ) : (
                  <Typography
                    variant="body"
                  >
                    {selectedPatient?.user?.name}
                  </Typography>
                )}
              </Box>
            </Box>
            <BaseImage 
              src={IconMedicine}
              sx={{
                minWidth: '149px',
                width: '149px',
                height: '149px',
                position: 'absolute',
                right: 0,
                top: { xs: 50, md: 100 },
                opacity: 0.4
              }}
            />
          </Box>
        </BlockContainer>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'primary.mainBlue',
          width: '100%',
          height: '63px',
          display: 'none',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-start' },
          px: '20px'
        }}
      >
        <BlockContainer>
          <Box
            sx={{
              backgroundColor: 'primary.mainBlue',
              width: '100%',
              height: '63px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-start' },
              px: '12px'
            }}
          >
          {TAB_REPORT.map(((tab, idx) => (
            <Fragment key={tab.title}>
              <Box
                sx={{
                  borderRadius: '40px',
                  width: '131px',
                  height: '40px',
                  backgroundColor: tab.title === selectedTab ? 'primary.white' : 'primary.mainBlue',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedTab(tab.title)}
              >
                <Typography
                  variant="body"
                  color={tab.title === selectedTab ? 'primary.black' : 'white'}
                >
                  {tab.title}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: idx === 0 ? 'block' : 'none',
                  width: '1px',
                  height:' 15px',
                  backgroundColor: "primary.mediumBlue",
                  mx: '32px'
                }}
              />
            </Fragment>
          )))}
          </Box>
        </BlockContainer>
      </Box>
      {selectedTab === 'Summary' && (
        <Box
          sx={{
            minHeight: 'calc(100vh - 383px)',
            background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
            padding: '0 20px 24px',
            position: 'relative',
          }}
        >
          <BlockContainer>
            <MedicationSummary
              onClickMedication={handleClickRiskCounts}
              medicationData={medicationData}
              medications={currentMedications}
              onChangeTab={() => setSelectedTab('Details')}
              onClickSearch={handleClickSearch}
            />
          </BlockContainer>
        </Box>
      )}
      {selectedTab === 'Details' && (
        <Box
          sx={{
            minHeight: 'calc(100vh - 383px)',
            background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
            padding: { xs: '0', md: '37px 20px' },
            position: 'relative'
          }}
        >
          <BlockContainer>
            <MedicationDetails
              medicationData={medicationData}
              medications={medications}
              selectedLevel={selectedLevel}
            />
          </BlockContainer>
        </Box>
      )}
      <ReportReviewBottomNav reportDetail={medicationData} />
    </MainContainer>
  )
}

export default MedicationReport